"use client";

import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Input } from "./Form";
import useDebounce from "../../common/utils/useDebounce";
import Icon from "../../common/components/Icon";

export interface SearchFieldProps {
  placeholder: string;
  onSearch?: (searchValue?: string) => void;
  defaultValue?: string;
  alwaysShowReset?: boolean;
  updateOnChange?: boolean;
  autoFocus?: boolean;
  onEnter?: (e?: any) => void; // Add onKeyUp prop
  onFocus?: (e?: any) => void; // Add onFocus prop
  onBlur?: (e?: any) => void; // Add onBlur prop
}

const SearchField = React.forwardRef<{ reset }, SearchFieldProps>(
  (
    {
      placeholder,
      defaultValue,
      autoFocus,
      alwaysShowReset,
      updateOnChange,
      onSearch,
      onEnter,
      onFocus,
      onBlur,
    },
    ref,
  ) => {
    const fireOnSearchDebounced = useDebounce(onSearch);
    const [value, setValue] = useState(defaultValue);

    const reset = useCallback(() => {
      setValue("");
      onSearch?.(null);
    }, [setValue, onSearch]);

    useImperativeHandle(ref, () => {
      return {
        reset,
      };
    }, [reset]);

    const onChange = (e) => {
      setValue(e.target.value);
      if (updateOnChange) {
        fireOnSearchDebounced(e.target.value);
      }
    };

    const onKeyUpHandler = (e) => {
      e.preventDefault();
      if (e.keyCode === 13) {
        onSearch(e.target.value);
        onEnter?.();
      }
    };

    const handleSearchClicked = (e) => {
      e.preventDefault();
      onSearch(value);
      onEnter?.();
    };
    useEffect(() => {
      if (defaultValue !== value) {
        setValue(defaultValue);
      }
    }, [defaultValue]);

    return (
      <div className="form-field-input-wrapper position-relative">
        <Input
          type="text"
          ref={ref}
          placeholder={placeholder}
          value={value || ""}
          autoFocus={autoFocus}
          onChange={onChange}
          onKeyUp={onKeyUpHandler}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        {(value || alwaysShowReset) && (
          <span
            className="position-absolute top-50 end-0 translate-middle-y"
            style={{ marginTop: "2px", marginRight: "37px" }}
          >
            <button onClick={reset} className="d-inline-flex">
              <Icon icon="close" />
            </button>
          </span>
        )}
        <span
          className="position-absolute top-50 end-0 translate-middle-y"
          style={{ marginTop: "2px", marginRight: "5px" }}
        >
          {!updateOnChange ? (
            <button onClick={handleSearchClicked}>
              <Icon icon="search" />
            </button>
          ) : (
            <Icon icon="search" />
          )}
        </span>
      </div>
    );
  },
);

SearchField.displayName = "SearchField";

export default SearchField;
